import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Activity, Rss, Loader2, AlertCircle, RefreshCw,
  ExternalLink, Sparkles, Copy, Check, MessageSquare,
  Lightbulb, Radio, Signal, BarChart3,
  CheckCircle2, AlertTriangle, Plus,
} from 'lucide-react';
import { fetchTrendFeed, agentChain } from '../../lib/notion-api';

// ═══ 키워드 분석기 ═══
function Sparkline({ trend, color }) {
  if (!trend || trend.length < 2) return null;
  const max = Math.max(...trend.map(p => p.ratio), 1);
  const W = 240, H = 44, pad = 4;
  const pts = trend.map((p, i) => {
    const x = pad + (i / (trend.length - 1)) * (W - pad * 2);
    const y = H - pad - (p.ratio / max) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const lastPt = trend[trend.length - 1];
  const lastX = W - pad;
  const lastY = H - pad - (lastPt.ratio / max) * (H - pad * 2);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="n-kw-sparkline" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
    </svg>
  );
}

function KeywordResult({ result, onClose }) {
  const { main, suggestions } = result;
  if (!main) return null;

  const arrow    = main.diff > 1 ? '▲' : main.diff < -1 ? '▼' : '→';
  const diffColor = main.diff > 1 ? 'var(--green)' : main.diff < -1 ? 'var(--red)' : 'var(--txt3)';

  return (
    <div className="n-kw-result-card">
      {/* 키워드 + 닫기 */}
      <div className="n-kw-result-header">
        <span className="n-kw-result-title">"{main.keyword}"</span>
        <button className="n-kw-close" onClick={onClose}>✕</button>
      </div>

      {/* 등급 뱃지 + 검색지수 */}
      <div className="n-kw-grade-row">
        <span className="n-kw-grade-badge" style={{ background: main.color }}>{main.grade}등급</span>
        <span className="n-kw-grade-label" style={{ color: main.color }}>{main.label}</span>
        <span className="n-kw-grade-diff" style={{ color: diffColor }}>{arrow} {main.diff >= 0 ? '+' : ''}{main.diff}</span>
        <span className="n-kw-grade-ratio" style={{ color: 'var(--txt2)' }}>지수 {main.ratio}</span>
      </div>

      {/* 검색량 바 */}
      <div className="n-kw-bar-track">
        <div className="n-kw-bar-fill" style={{ width: `${main.ratio}%`, background: main.color }} />
      </div>

      {/* 30일 트렌드 차트 */}
      {main.trend?.length > 1 && (
        <div className="n-kw-chart-wrap">
          <span className="n-kw-chart-label">최근 {main.trend.length}주 트렌드</span>
          <Sparkline trend={main.trend} color={main.color} />
        </div>
      )}

      {/* 판정 */}
      <div className="n-kw-verdict-box" style={{ borderColor: `${main.color}40`, background: `${main.color}10` }}>
        <span className="n-kw-verdict-icon">
          {main.recommend
            ? <CheckCircle2 size={16} style={{ color: 'var(--green)' }} />
            : <AlertTriangle size={16} style={{ color: 'var(--orange)' }} />}
        </span>
        <div>
          <div className="n-kw-verdict-title" style={{ color: main.color }}>
            {main.recommend ? '써볼 만한 키워드' : '쓰기 어려운 키워드'}
          </div>
          <div className="n-kw-verdict-text">{main.verdict}</div>
        </div>
      </div>

      {/* 연관 키워드 */}
      {suggestions.length > 0 && (
        <div className="n-kw-suggestions">
          <div className="n-kw-suggest-header"><Lightbulb size={13} style={{ marginRight: 5, color: 'var(--gold)' }} />대신 이런 키워드는 어때요?</div>
          {suggestions.map((s, i) => (
            <div key={i} className="n-kw-suggest-row">
              <span className="n-kw-suggest-grade" style={{ color: s.color, borderColor: `${s.color}60` }}>{s.grade}</span>
              <span className="n-kw-suggest-name">"{s.keyword}"</span>
              <span className="n-kw-suggest-index" style={{ color: 'var(--txt3)' }}>지수 {s.ratio}</span>
              {s.recommend && <span className="n-kw-suggest-rec">← 추천</span>}
            </div>
          ))}
        </div>
      )}

      {/* 등급 기준표 */}
      <div className="n-kw-grade-table">
        {[
          { grade: 'S', range: '80+', label: '매우 치열, 비추천', color: 'var(--grade-s)' },
          { grade: 'A', range: '50–79', label: '경쟁 높음, 어려움', color: 'var(--grade-a)' },
          { grade: 'B', range: '20–49', label: '도전 가능', color: 'var(--grade-b)' },
          { grade: 'C', range: '20 미만', label: '경쟁 낮음, 추천', color: 'var(--grade-c)' },
        ].map(g => (
          <div key={g.grade} className={`n-kw-grade-row-table ${main.grade === g.grade ? 'active' : ''}`}
            style={main.grade === g.grade ? { borderColor: g.color, background: `${g.color}12` } : {}}>
            <span className="n-kw-gt-grade" style={{ color: g.color }}>{g.grade}</span>
            <span className="n-kw-gt-range">{g.range}</span>
            <span className="n-kw-gt-label">{g.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KeywordAnalyzer() {
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const inputRef = React.useRef(null);

  const analyze = async () => {
    const kw = input.trim();
    if (!kw || loading) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await fetch(`/api/keyword-analyze?keyword=${encodeURIComponent(kw)}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="n-kw-analyzer">
      <div className="n-kw-search-row">
        <input
          ref={inputRef}
          className="n-kw-input"
          value={input}
          onChange={e => { setInput(e.target.value); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && analyze()}
          placeholder="키워드를 입력하세요 (예: 맛집추천)"
          disabled={loading}
        />
        <button className="n-kw-analyze-btn" onClick={analyze} disabled={loading || !input.trim()}>
          {loading ? <Loader2 size={13} className="n-spin" /> : '분석하기'}
        </button>
      </div>
      {error && (
        <div className="n-error" style={{ margin: '8px 0', fontSize: 11 }}>
          <AlertCircle size={11} /> {error}
        </div>
      )}
      {result && <KeywordResult result={result} onClose={() => setResult(null)} />}
    </div>
  );
}

// ═══ 내 키워드 모니터링 ═══
const MONITOR_KEY = 'caio-monitor-keywords';

function loadMonitorKeywords() {
  try { return JSON.parse(localStorage.getItem(MONITOR_KEY) || '[]'); } catch { return []; }
}
function saveMonitorKeywords(kws) {
  localStorage.setItem(MONITOR_KEY, JSON.stringify(kws));
}

function KeywordMonitor() {
  const [keywords, setKeywords] = React.useState(loadMonitorKeywords);
  const [input, setInput] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [registering, setRegistering] = React.useState({}); // { [keyword]: 'loading'|'done'|null }

  const registerContent = async (keyword) => {
    if (registering[keyword]) return;
    setRegistering(prev => ({ ...prev, [keyword]: 'loading' }));
    try {
      const res = await fetch('/api/notion-add-todo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: `${keyword} 콘텐츠 작성 (키워드 트렌드 대응)` }),
      });
      const data = await res.json();
      if (data.success) setRegistering(prev => ({ ...prev, [keyword]: 'done' }));
      else throw new Error(data.error);
    } catch { setRegistering(prev => ({ ...prev, [keyword]: null })); }
  };
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const inputRef = React.useRef(null);

  const fetchMonitor = React.useCallback(async (kws) => {
    if (!kws.length) { setItems([]); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/keyword-monitor?keywords=${encodeURIComponent(kws.join(','))}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setItems(data.results || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchMonitor(keywords); }, []);

  const addKeyword = () => {
    const kw = input.trim();
    if (!kw || keywords.includes(kw) || keywords.length >= 5) return;
    const next = [...keywords, kw];
    setKeywords(next); saveMonitorKeywords(next);
    setInput(''); inputRef.current?.focus();
    fetchMonitor(next);
  };

  const removeKeyword = (kw) => {
    const next = keywords.filter(k => k !== kw);
    setKeywords(next); saveMonitorKeywords(next);
    fetchMonitor(next);
  };

  return (
    <div className="n-kw-monitor">
      <div className="n-section-title" style={{ padding: '4px 4px 8px', marginBottom: 0, fontSize: 11 }}>
        <Activity size={12} /> 내 키워드 모니터링
        <span style={{ fontSize: 10, color: 'var(--txt3)', fontWeight: 400, marginLeft: 6, textTransform: 'none', letterSpacing: 0 }}>
          최대 5개 · DataLab 4주 추이
        </span>
        {keywords.length > 0 && (
          <button className="n-section-link" onClick={() => fetchMonitor(keywords)} style={{ marginLeft: 'auto' }}>
            {loading ? <Loader2 size={10} className="n-spin" /> : <RefreshCw size={10} />}
          </button>
        )}
      </div>

      {/* 등록된 키워드 칩 */}
      <div className="n-kw-monitor-chips">
        {keywords.map(kw => (
          <span key={kw} className="n-kw-monitor-chip">
            {kw}
            <button className="n-kw-monitor-chip-del" onClick={() => removeKeyword(kw)} aria-label={`${kw} 삭제`}>×</button>
          </span>
        ))}
        {keywords.length === 0 && (
          <span style={{ fontSize: 11, color: 'var(--txt3)' }}>등록된 키워드가 없습니다</span>
        )}
      </div>

      {/* 키워드 입력 */}
      {keywords.length < 5 && (
        <div className="n-kw-monitor-input-row">
          <input
            ref={inputRef}
            className="n-kw-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addKeyword()}
            placeholder="키워드 추가 (Enter)"
            style={{ fontSize: 12, height: 32 }}
          />
          <button className="n-kw-analyze-btn" onClick={addKeyword} disabled={!input.trim()} style={{ height: 32, fontSize: 12 }}>
            추가
          </button>
        </div>
      )}

      {/* 결과 */}
      {error && <div className="n-error" style={{ margin: '8px 0', fontSize: 11 }}><AlertCircle size={11} /> {error}</div>}
      {loading && (
        <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--txt3)', fontSize: 12 }}>
          <Loader2 size={14} className="n-spin" style={{ marginRight: 6, verticalAlign: 'middle' }} />데이터랩 조회 중…
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="n-kw-monitor-results">
          {items.map(item => {
            const isRising = item.diff > 1;
            const regState = registering[item.title];
            return (
              <div key={item.title} className="n-kw-monitor-row" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                  <div className="n-kw-monitor-row-left">
                    <span className="n-kw-monitor-title">{item.title}</span>
                    <span className="n-kw-monitor-desc">{item.desc}</span>
                  </div>
                  <div className="n-kw-monitor-row-right">
                    <span className="n-kw-monitor-advice" style={{ color: item.adviceColor }}>{item.advice}</span>
                  </div>
                </a>
                {isRising && (
                  <button
                    className="n-kw-reg-btn"
                    onClick={() => registerContent(item.title)}
                    disabled={!!regState}
                    title="이 키워드로 콘텐츠 할일 등록"
                  >
                    {regState === 'loading' ? <Loader2 size={9} className="n-spin" /> : regState === 'done' ? <CheckCircle2 size={9} /> : <Plus size={9} />}
                    {regState === 'done' ? '등록됨' : '콘텐츠'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══ 트렌드 모니터링 (4개 탭) ═══
const SRC_COLOR = {
  search: 'var(--green)', blog: 'var(--accent)', influencer: 'var(--gold)', creator: 'var(--orange)',
  gnews_nblog: 'var(--green)', gnews_infl: 'var(--gold)', gnews_ai: 'var(--accent)',
  gnews_seo: 'var(--agent-booker)', gnews_algo: 'var(--agent-thread)',
  bloter: 'var(--agent-booker)', inews24: 'var(--accent)', etnews: 'var(--green)',
  naver_datalab: 'var(--naver-green)',
};
const TABS = [
  { key: 'naver',    label: '네이버 공식', Icon: Signal },
  { key: 'industry', label: '업계 레이더', Icon: Radio },
  { key: 'tech',     label: 'IT 업계',    Icon: Lightbulb },
  { key: 'trends',   label: '키워드 분석', Icon: BarChart3 },
];

// ── 탭 이동해도 분석 유지 (module-level 전역 상태) ──
const _analysisCache     = {}; // { [tabKey]: result }
const _analysisLoading   = {}; // { [tabKey]: boolean }
const _analysisError     = {}; // { [tabKey]: string|null }
const _analysisCancelled = {}; // { [tabKey]: boolean }
const _analysisListeners = {}; // { [tabKey]: Set<fn> }
// 이전 세션 트렌드 분석 복원 (6시간 이내)
try {
  ['naver','industry','tech','trends'].forEach(k => {
    const saved = localStorage.getItem('caio_trend_' + k);
    if (saved) {
      const { result, ts } = JSON.parse(saved);
      if (Date.now() - ts < 6 * 60 * 60 * 1000) _analysisCache[k] = result;
    }
  });
} catch {}

function _subscribe(tabKey, cb) {
  if (!_analysisListeners[tabKey]) _analysisListeners[tabKey] = new Set();
  _analysisListeners[tabKey].add(cb);
}
function _unsubscribe(tabKey, cb) {
  _analysisListeners[tabKey]?.delete(cb);
}
function _notify(tabKey) {
  _analysisListeners[tabKey]?.forEach(cb => cb());
}

// ── 오피스 탭과 상태 공유 (탭 마운트 시점 무관) ──
function _syncAgentWork(agent, working) {
  if (!window.__caioAgentState) window.__caioAgentState = { agents: {}, meeting: null };
  window.__caioAgentState.agents[agent] = working;
  window.dispatchEvent(new CustomEvent('caio-agent-work', { detail: { agent, working } }));
}
function _syncMeeting(detail) {
  if (!window.__caioAgentState) window.__caioAgentState = { agents: {}, meeting: null };
  window.__caioAgentState.meeting = detail.active ? detail : null;
  window.dispatchEvent(new CustomEvent('caio-meeting', { detail }));
}
const TREND_TEAM = ['trendy', 'dash', 'sage'];

function TrendList({ items, readIds, onMarkRead, onExpand, expanded }) {
  const fmt = (d) => { if (!d) return ''; const t = new Date(d); return `${t.getMonth()+1}/${t.getDate()}`; };
  return (
    <div className="n-trend-list">
      {items.slice(0, 10).map((item, i) => {
        const isNew = !readIds.has(item.link);
        const isOpen = expanded === item.link;
        // 키워드 분석 탭 — 검색지수 + 한 줄 해석
        if (item.isKeyword) {
          const barPct = Math.min(100, Math.round(item.ratio || 0));
          const diffNum = item.diff || 0;
          const arrow = diffNum > 1 ? '▲' : diffNum < -1 ? '▼' : '→';
          const diffColor = diffNum > 1 ? 'var(--green)' : diffNum < -1 ? 'var(--red)' : 'var(--txt3)';
          return (
            <div key={i} className="n-kw-card">
              <div className="n-kw-header">
                <span className="n-kw-title">{item.title}</span>
                <span className="n-kw-diff" style={{ color: diffColor }}>
                  {arrow} {diffNum >= 0 ? '+' : ''}{diffNum.toFixed(1)}
                </span>
              </div>
              {/* 해석 뱃지 */}
              <div className="n-kw-advice" style={{ color: item.adviceColor || 'var(--txt2)' }}>
                {item.advice}
              </div>
              {/* 검색지수 바 */}
              <div className="n-kw-bar-wrap">
                <div className="n-kw-bar" style={{ width: `${barPct}%`, background: item.adviceColor || 'var(--gold)' }} />
                <span className="n-kw-bar-label">{barPct}</span>
              </div>
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="n-kw-link">
                <ExternalLink size={10} /> 데이터랩에서 보기
              </a>
            </div>
          );
        }
        return (
          <div key={i} className={`n-trend-item-wrap ${isNew ? 'new' : 'read'}`}>
            <div className="n-trend-item-row" onClick={() => { onExpand(isOpen ? null : item.link); onMarkRead(item.link); }}
              role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && (() => { onExpand(isOpen ? null : item.link); onMarkRead(item.link); })()}
              aria-expanded={isOpen}>
              <span className="n-trend-item-source" style={{ color: SRC_COLOR[item.sourceId] || 'var(--txt3)' }}>{item.source}</span>
              <span className="n-trend-item-title">{item.title}</span>
              <span className="n-trend-item-date">{fmt(item.pubDate)}</span>
              <span className="n-trend-item-chevron">{isOpen ? '▲' : '▼'}</span>
              {isNew && <span className="n-trend-dot-new" />}
            </div>
            {isOpen && (
              <div className="n-trend-item-detail">
                {item.desc ? <p className="n-trend-desc">{item.desc}</p>
                  : <p className="n-trend-desc" style={{ color: 'var(--txt3)' }}>본문 요약 없음</p>}
                <a href={item.link} target="_blank" rel="noopener noreferrer" className="n-trend-read-btn">
                  <ExternalLink size={11} /> 원문 읽기
                </a>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const TAB_LABEL = { naver: '네이버 공식', industry: '업계 레이더', tech: 'IT 업계', trends: '트렌드 키워드' };

function TrendAnalyzer({ items, tabKey }) {
  // 로컬 state는 module-level 값을 반영하는 mirror
  const [result,  setResult]  = React.useState(() => _analysisCache[tabKey]   || null);
  const [loading, setLoading] = React.useState(() => !!_analysisLoading[tabKey]);
  const [error,   setError]   = React.useState(() => _analysisError[tabKey]   || null);
  const [copied,  setCopied]  = React.useState(null);

  // 마운트 시 구독 → 탭 전환 후 돌아와도 최신 상태 즉시 반영
  React.useEffect(() => {
    const sync = () => {
      setResult(_analysisCache[tabKey]   || null);
      setLoading(!!_analysisLoading[tabKey]);
      setError(_analysisError[tabKey]    || null);
    };
    sync(); // 마운트 직후 동기화
    _subscribe(tabKey, sync);
    return () => _unsubscribe(tabKey, sync);
  }, [tabKey]);

  const stopMeeting = () => {
    _analysisCancelled[tabKey] = true;
    _analysisLoading[tabKey]   = false;
    _analysisError[tabKey]     = null;
    _notify(tabKey);
    TREND_TEAM.forEach(agent => _syncAgentWork(agent, false));
    _syncMeeting({ active: false });
  };

  const analyze = async () => {
    if (!items.length || _analysisLoading[tabKey]) return;

    _analysisLoading[tabKey]   = true;
    _analysisError[tabKey]     = null;
    _analysisCancelled[tabKey] = false;
    _notify(tabKey);

    TREND_TEAM.forEach(agent => _syncAgentWork(agent, true));
    _syncMeeting({ active: true, mission: 'trend', agents: TREND_TEAM, label: '트렌드 분석 미팅' });

    const isKeywords = tabKey === 'trends';
    const headlines = items.slice(0, 10).map((it, i) => {
      if (isKeywords) return `${i + 1}. ${it.title}${it.desc ? ' ('+it.desc+')' : ''}`;
      return `${i + 1}. [${it.source}] ${it.title}${it.desc ? ' — ' + it.desc.slice(0, 80) : ''}\n   출처: ${it.link}`;
    }).join('\n');

    const command = isKeywords
      ? `다음은 구글 트렌드 KR 실시간 인기 검색어입니다:\n\n${headlines}\n\n이 키워드들을 분석해서 아래 형식으로 정리해줘. 우리는 네이버 블로그 교육 아카데미(블로그오빠)를 운영 중이고, 블로그 크루 86명, VIP 멤버십, 인플루언서 양성 프로그램을 운영해.\n\n1. 지금 뜨는 키워드 트렌드 요약 (2~3줄)\n2. 블로그 소재로 쓸 수 있는 키워드 TOP 3 (이유 포함)\n3. 즉시 실행 액션 2~3가지\n4. 크루 단톡방 공지글 (자연스러운 말투, 100자 내외, 키워드 활용 방법 포함)`
      : `다음은 "${TAB_LABEL[tabKey]}" 최신 뉴스/공지 헤드라인입니다:\n\n${headlines}\n\n위 내용을 분석해서 아래 형식으로 정리해줘. 우리는 네이버 블로그 교육 아카데미(블로그오빠)를 운영 중이고, 블로그 크루 86명, 스마트스토어, VIP 멤버십, 인플루언서 양성 프로그램을 운영해.\n\n1. 핵심 변화 요약 (2~3줄)\n2. 우리 비즈니스에 주는 의미 (구체적으로)\n3. 즉시 실행 액션 2~3가지\n4. 크루 단톡방 공지글 (자연스러운 말투, 100자 내외, 행동 유도 포함)`;

    const usedSources = isKeywords ? [] : items.slice(0, 10).map(it => ({ title: it.title, link: it.link, source: it.source }));

    try {
      const res = await agentChain(command);
      if (!_analysisCancelled[tabKey]) {
        _analysisCache[tabKey] = { ...res, usedSources };
        try { localStorage.setItem('caio_trend_' + tabKey, JSON.stringify({ result: _analysisCache[tabKey], ts: Date.now() })); } catch {}
      }
    } catch (e) {
      if (!_analysisCancelled[tabKey]) _analysisError[tabKey] = e.message;
    } finally {
      _analysisLoading[tabKey] = false;
      _notify(tabKey);
      TREND_TEAM.forEach(agent => _syncAgentWork(agent, false));
      _syncMeeting({ active: false });
    }
  };

  const copyText = async (text, idx) => {
    try { await navigator.clipboard.writeText(text); }
    catch { const el=document.createElement('textarea');el.value=text;el.style.cssText='position:fixed;opacity:0;top:0;left:0;';document.body.appendChild(el);el.focus();el.select();try{document.execCommand('copy');}catch{}document.body.removeChild(el); }
    setCopied(idx); setTimeout(() => setCopied(null), 1500);
  };

  // 결과 텍스트에서 단톡방 공지글 추출
  const extractNotice = (content) => {
    if (!content) return null;
    const m = content.match(/단톡방[^:：]*[:：]\s*([\s\S]+?)(?:\n\n|$)/i)
      || content.match(/공지글[^:：]*[:：]\s*([\s\S]+?)(?:\n\n|$)/i);
    return m ? m[1].trim() : null;
  };

  return (
    <div className="n-trend-analyzer">
      {!result && !loading && (
        <button className="n-trend-analyze-btn" onClick={analyze} disabled={!items.length}>
          <Sparkles size={13} /> AI 분석 + 단톡방 공지글 작성
        </button>
      )}
      {loading && (
        <div className="n-goal-loading">
          <Loader2 size={13} className="n-spin" /><span>트렌디 에이전트 분석 중…</span>
          <button className="n-stop-btn" onClick={stopMeeting}>중지</button>
        </div>
      )}
      {error && <div className="n-chain-error"><AlertCircle size={12} /> {error}</div>}
      {result && !loading && (
        <div className="n-trend-analysis">
          <div className="n-trend-analysis-header">
            <Sparkles size={12} /><span>AI 트렌드 분석</span>
            <button className="n-section-link" onClick={analyze}><RefreshCw size={10} /> 재분석</button>
            <button className="n-section-link" onClick={() => { delete _analysisCache[tabKey]; _notify(tabKey); }}>닫기</button>
          </div>
          {result.chain && (
            <div className="n-chain-flow" style={{ marginBottom: 10 }}>
              {result.chain.map((a, i) => (
                <span key={i}>
                  <span className="n-chain-agent">{a}</span>
                  {i < result.chain.length - 1 && <span className="n-chain-arrow">→</span>}
                </span>
              ))}
            </div>
          )}
          {result.final?.map((item, i) => {
            const notice = extractNotice(item.content);
            return (
              <div key={i} className="n-trend-analysis-item">
                <div className="n-trend-analysis-title">{item.title}</div>
                <div className="n-trend-analysis-body">{item.content}</div>
                {notice && (
                  <div className="n-trend-notice-box">
                    <div className="n-trend-notice-label"><MessageSquare size={11} /> 단톡방 공지글</div>
                    <div className="n-trend-notice-text">{notice}</div>
                    <button className="n-ai-copy small" onClick={() => copyText(notice, i)}>
                      {copied === i ? <><Check size={10} /> 복사됨</> : <><Copy size={10} /> 복사</>}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {result.usedSources?.length > 0 && (
            <div className="n-trend-sources-used">
              <div className="n-trend-sources-used-label"><ExternalLink size={10} /> 분석 출처</div>
              {result.usedSources.map((s, i) => (
                <a key={i} href={s.link} target="_blank" rel="noopener noreferrer" className="n-trend-source-link">
                  <span className="n-trend-source-name">[{s.source}]</span> {s.title}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NaverTrend() {
  const [feed, setFeed] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('naver');
  const [filterSource, setFilterSource] = React.useState(null);
  const [expanded, setExpanded] = React.useState(null);
  const [readIds, setReadIds] = React.useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('caio-trend-read') || '[]')); } catch { return new Set(); }
  });

  const load = () => {
    setLoading(true); setError(null);
    fetchTrendFeed().then(setFeed).catch(e => setError(e.message)).finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, []);

  const markRead = (link) => {
    setReadIds(prev => {
      const next = new Set([...prev, link]);
      localStorage.setItem('caio-trend-read', JSON.stringify([...next]));
      return next;
    });
  };

  const allItems = feed?.byCategory?.[activeTab] || [];
  const sources = feed?.sourceStatus?.[activeTab] || [];
  const items = filterSource ? allItems.filter(i => i.sourceId === filterSource) : allItems;
  const totalNew = feed ? Object.values(feed.byCategory || {}).flat().filter(i => !readIds.has(i.link)).length : 0;

  const toggleFilter = (srcId) => {
    setFilterSource(prev => prev === srcId ? null : srcId);
    setExpanded(null);
  };

  return (
    <div className="n-card" style={{ marginBottom: 16, padding: '16px 0 0' }}>
      {/* 헤더 */}
      <div className="n-section-title" style={{ padding: '0 20px', marginBottom: 10 }}>
        <Rss size={14} /><span>트렌드 모니터링</span>
        {totalNew > 0 && <span className="n-trend-new-badge">{totalNew} NEW</span>}
        <button className="n-section-link" onClick={load} style={{ marginLeft: 'auto' }}>
          {loading ? <Loader2 size={11} className="n-spin" /> : <RefreshCw size={11} />}
        </button>
      </div>

      {/* 탭 */}
      <div className="n-trend-tabs">
        {TABS.map(t => {
          const tabItems = feed?.byCategory?.[t.key] || [];
          const newCnt = tabItems.filter(i => !readIds.has(i.link)).length;
          return (
            <button key={t.key} className={`n-trend-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => { setActiveTab(t.key); setFilterSource(null); setExpanded(null); }}>
              <span className="n-trend-tab-icon"><t.Icon size={12} strokeWidth={1.8} /></span>
              {t.label}
              {newCnt > 0 && <span className="n-trend-tab-dot" />}
            </button>
          );
        })}
      </div>

      {/* 소스 필터 칩 */}
      {sources.length > 1 && (
        <div style={{ padding: '8px 20px 6px' }}>
          <div className="n-trend-sources-row">
            <button
              className={`n-trend-src-chip filter-btn ${!filterSource ? 'active' : ''}`}
              style={{ '--src-color': 'var(--txt2)' }}
              onClick={() => { setFilterSource(null); setExpanded(null); }}>
              전체
            </button>
            {sources.map(s => {
              const isActive = filterSource === s.id;
              const srcNewCnt = allItems.filter(i => i.sourceId === s.id && !readIds.has(i.link)).length;
              return (
                <button key={s.id}
                  className={`n-trend-src-chip filter-btn ${s.ok ? 'ok' : 'err'} ${isActive ? 'active' : ''}`}
                  style={{ '--src-color': SRC_COLOR[s.id] || 'var(--cat-ops)' }}
                  onClick={() => toggleFilter(s.id)}>
                  <i className="n-trend-src-dot" />{s.name}
                  {srcNewCnt > 0 && <span className="n-trend-src-new">{srcNewCnt}</span>}
                </button>
              );
            })}
          </div>
          {filterSource && (
            <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 4 }}>
              {sources.find(s => s.id === filterSource)?.name} 기사만 표시 중 · 전체 버튼으로 초기화
            </div>
          )}
        </div>
      )}

      {/* 피드 */}
      <div style={{ padding: '0 8px 12px' }}>
        {error && <div className="n-error" style={{ margin: '0 12px', fontSize: 11 }}><AlertCircle size={12} /> {error}</div>}
        {!loading && items.length === 0 && !error && activeTab !== 'trends' && (
          <div style={{ padding: '16px 12px', fontSize: 12, color: 'var(--txt3)', textAlign: 'center' }}>
            {filterSource ? '해당 소스의 기사가 없습니다' : '피드를 불러오는 중이거나 항목이 없습니다'}
          </div>
        )}
        {/* 키워드 분석 탭: 검색 분석기 + 모니터링 */}
        {activeTab === 'trends' ? (
          <div style={{ padding: '0 4px 4px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <KeywordAnalyzer />
            <KeywordMonitor />
          </div>
        ) : (
          <>
            {items.length > 0 && (
              <>
                <TrendList items={items} readIds={readIds} onMarkRead={markRead} onExpand={setExpanded} expanded={expanded} />
                <TrendAnalyzer items={items} tabKey={activeTab} />
              </>
            )}
          </>
        )}
      </div>

      {feed?.fetchedAt && (
        <div style={{ fontSize: 10, color: 'var(--txt3)', textAlign: 'right', padding: '4px 20px 12px' }}>
          {new Date(feed.fetchedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} 기준
        </div>
      )}
    </div>
  );
}

export { NaverTrend, KeywordMonitor };
